'use client';

import { type ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { cn } from '@fshr/shared-react';
import Carousel from '../carousel';
import getCtaStyles from '../button/get-cta-styles';
import RichTextTitle from '../rich-text-title';
import BevSafeText from '../bev-safe-text';
import ExperienceFeatureCard from './experience-feature-card';
import ExperienceProductCard from './experience-product-card';
import { useTranslations } from '@fshr/context/i18n-context';
import {
	availabilityContextFromStores,
	withLiveAvailability,
	type ExperiencesGridStores,
} from './live-product-data';
import type {
	AvailabilityContext,
	ExperienceFeatureCardData,
	ExperienceProductCardData,
} from './types';

// At exactly three product cards the carousel collapses to a static 3-column grid
// with no arrows or pagination (per AC). Below md it stays a swipeable rail so the
// cards are not stacked vertically.
const STATIC_GRID_COUNT = 3;
const MAX_CARDS = 9;

export type Props = ExperiencesGridStores & {
	title: string;
	id?: string;
	interactionType?: string;
	sectionId?: string;
	description?: string;
	ctaLabel?: string;
	ctaHref?: string;
	ctaNewWindow?: boolean;
	featureCard?: ExperienceFeatureCardData;
	productCards?: ExperienceProductCardData[];
	// Opt-in: passed straight through to each ExperienceProductCard's own showCategory prop.
	showProductCategory?: boolean;
	availabilityContext?: AvailabilityContext;
	previousLabel?: string;
	nextLabel?: string;
	paginationLabel?: string;
	className?: string;
};

function ExperiencesGrid({
	title,
	id,
	interactionType,
	sectionId,
	description,
	ctaLabel,
	ctaHref,
	ctaNewWindow,
	featureCard,
	productCards,
	showProductCategory,
	availabilityContext,
	productAvailabilityStore,
	cartStore,
	bookingsStore,
	userStore,
	previousLabel,
	nextLabel,
	paginationLabel,
	className,
}: Props): ReactElement | null {
	const t = useTranslations();
	const stores: ExperiencesGridStores = {
		productAvailabilityStore,
		cartStore,
		bookingsStore,
		userStore,
	};
	const resolvedContext = availabilityContext ?? availabilityContextFromStores(stores);
	const cards = (productCards ?? [])
		.slice(0, MAX_CARDS)
		.map(card => withLiveAvailability(card, stores));
	const resolvedFeatureCard = featureCard ? withLiveAvailability(featureCard, stores) : undefined;
	const hasFeature = Boolean(resolvedFeatureCard);
	const hasCarousel = cards.length > 0;

	// Each element can be hidden; render nothing if neither is configured.
	if (!hasFeature && !hasCarousel) return null;

	const showCta = Boolean(ctaLabel && ctaHref);
	const isStaticGrid = cards.length === STATIC_GRID_COUNT;

	const productNodes = cards.map(card => (
		<ExperienceProductCard
			key={card.id}
			card={card}
			productAvailabilityStore={productAvailabilityStore}
			userStore={userStore}
			availabilityContext={resolvedContext}
			interactionType={interactionType}
			showCategory={showProductCategory}
		/>
	));

	return (
		<section
			id={sectionId}
			aria-labelledby={title && id ? id : undefined}
			className={cn(
				// Heading-block-to-carousel gap: Figma "Experiences grid" itemSpacing
				// is 20px mobile / 30px desktop — exact match for stack-l. The
				// section spans its parent uncapped so the card track can bleed to
				// the parent's edges (IAI-328); the header and feature card sit on
				// the wide grid themselves (gutter + cap + centring) so they line up
				// with their capped neighbours past each tier's cap (IAI-329,
				// decision b96ff0).
				'flex w-full flex-col gap-y-stack-l',
				className
			)}
		>
			{/* Title-to-CTA row: title-to-description is bound to heading-s in
			    both Figma frames (15px mobile / 20px desktop). The outer title-block-to-cta gap
			    (this row's own gap) is 20px mobile / 30px desktop (stack-l) —
			    bare form since md:justify-between already governs the visual
			    desktop gap; the row never wraps so the same value on both axes
			    is safe. */}
			<div className="flex w-full flex-col items-start gap-stack-l spacing-wide-grid md:flex-row md:items-end md:justify-between">
				<div className="flex flex-col gap-y-heading-s md:w-[723px]">
					<RichTextTitle
						title={title}
						id={id}
						className="text-display3 line-clamp-2 text-text-heading"
					/>
					{description && (
						<p className="text-h3 text-text-primary">
							<BevSafeText>{description}</BevSafeText>
						</p>
					)}
				</div>

				{showCta && (
					<a
						href={ctaHref}
						target={ctaNewWindow ? '_blank' : undefined}
						rel={ctaNewWindow ? 'noopener' : undefined}
						className={getCtaStyles({ intent: 'filled', size: 'small' })}
					>
						{ctaLabel}
					</a>
				)}
			</div>

			{resolvedFeatureCard && (
				<div className="w-full spacing-wide-grid">
					<ExperienceFeatureCard
						card={resolvedFeatureCard}
						productAvailabilityStore={productAvailabilityStore}
						userStore={userStore}
						availabilityContext={resolvedContext}
						interactionType={interactionType}
					/>
				</div>
			)}

			{hasCarousel &&
				(isStaticGrid ? (
					<div className="no-scrollbar flex snap-x snap-mandatory gap-x-grid-gutter overflow-x-auto max-md:spacing-wide-grid-bleed md:grid md:grid-cols-3 md:overflow-visible md:spacing-wide-grid-inset">
						{cards.map((card, index) => (
							<div key={card.id} className="w-[285px] shrink-0 snap-start md:w-auto">
								{productNodes[index]}
							</div>
						))}
					</div>
				) : (
					// Full bleed (IAI-328): the track runs to the section's edges with its
					// first and last cards on the content column; arrows and slider stay
					// on the content column. From lg the cards are four of the wide
					// grid's twelve columns — three across the content column (400px at
					// Figma's 1440) — which the track's container query width gives
					// directly, since its content box is the content column.
					<Carousel
						className="@container spacing-wide-grid-bleed"
						insetClassName="spacing-wide-grid-inset"
						groupChildrensClassName="lg:auto-cols-[calc((100cqw-2*var(--spacing-grid-gutter))/3)] xl:auto-cols-[calc((100cqw-2*var(--spacing-grid-gutter))/3)]"
						incrementAmount={{ sm: 1, md: 2, lg: 3, xl: 3 }}
						buttonsType="rounded"
						gapClassName="gap-inline-m md:gap-grid-gutter"
						showSlider
						sliderLabel={paginationLabel ?? t('Experiences carousel progress')}
						buttonLabels={{
							previous: previousLabel ?? t('Previous experiences'),
							next: nextLabel ?? t('Next experiences'),
						}}
					>
						{productNodes}
					</Carousel>
				))}
		</section>
	);
}

export default observer(ExperiencesGrid);
